'use client';

import {
  Close as SheetClosePrimitive,
  Content as SheetContentPrimitive,
  Description as SheetDescriptionPrimitive,
  Overlay as SheetOverlayPrimitive,
  Portal as SheetPortalPrimitive,
  Root as SheetRootPrimitive,
  Title as SheetTitlePrimitive,
  Trigger as SheetTriggerPrimitive,
} from '@radix-ui/react-dialog';
import { cva, type VariantProps } from 'class-variance-authority';
import { X } from 'lucide-react';
import type { ComponentPropsWithoutRef, ComponentRef, HTMLAttributes, Ref } from 'react';
import { cn } from '@/lib/utils';

const Sheet = SheetRootPrimitive;
const SheetTrigger = SheetTriggerPrimitive;
const SheetClose = SheetClosePrimitive;
const SheetPortal = SheetPortalPrimitive;

type SheetOverlayProperties = ComponentPropsWithoutRef<typeof SheetOverlayPrimitive> & {
  ref?: Ref<ComponentRef<typeof SheetOverlayPrimitive>> | undefined;
};

const SheetOverlay = ({ className, ref, ...properties }: SheetOverlayProperties) => (
  <SheetOverlayPrimitive
    ref={ref}
    className={cn(
      'fixed inset-0 z-layer-modal-backdrop bg-black/80  data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
      className
    )}
    {...properties}
  />
);

const sheetVariants = cva(
  'fixed z-layer-modal gap-4 bg-background p-6 shadow-lg transition ease-in-out data-[state=closed]:duration-300 data-[state=open]:duration-500 data-[state=open]:animate-in data-[state=closed]:animate-out',
  {
    variants: {
      side: {
        top: 'inset-x-0 top-0 border-b data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top',
        bottom:
          'inset-x-0 bottom-0 border-t data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom',
        left: 'inset-y-0 left-0 h-full w-3/4 border-r data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left sm:max-w-sm',
        right:
          'inset-y-0 right-0 h-full w-3/4 border-l data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right sm:max-w-sm',
      },
    },
    defaultVariants: {
      side: 'right',
    },
  }
);

type SheetContentProperties = ComponentPropsWithoutRef<typeof SheetContentPrimitive> &
  VariantProps<typeof sheetVariants> & {
    ref?: Ref<ComponentRef<typeof SheetContentPrimitive>> | undefined;
  };

const SheetContent = ({ side = 'right', className, children, ref, ...properties }: SheetContentProperties) => (
  <SheetPortal>
    <SheetOverlay />
    <SheetContentPrimitive ref={ref} className={cn(sheetVariants({ side }), className)} {...properties}>
      <SheetClose className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-secondary">
        <X className="h-4 w-4" />
        <span className="sr-only">Close</span>
      </SheetClose>
      {children}
    </SheetContentPrimitive>
  </SheetPortal>
);
SheetContent.displayName = SheetContentPrimitive.displayName;

const SheetHeader = ({ className, ...properties }: HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('flex flex-col space-y-2 text-center sm:text-left', className)} {...properties} />
);
SheetHeader.displayName = 'SheetHeader';

const SheetFooter = ({ className, ...properties }: HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2', className)} {...properties} />
);
SheetFooter.displayName = 'SheetFooter';

type SheetTitleProperties = ComponentPropsWithoutRef<typeof SheetTitlePrimitive> & {
  ref?: Ref<ComponentRef<typeof SheetTitlePrimitive>> | undefined;
};

const SheetTitle = ({ className, ref, ...properties }: SheetTitleProperties) => (
  <SheetTitlePrimitive ref={ref} className={cn('text-lg font-semibold text-foreground', className)} {...properties} />
);
SheetTitle.displayName = SheetTitlePrimitive.displayName;

type SheetDescriptionProperties = ComponentPropsWithoutRef<typeof SheetDescriptionPrimitive> & {
  ref?: Ref<ComponentRef<typeof SheetDescriptionPrimitive>> | undefined;
};

const SheetDescription = ({ className, ref, ...properties }: SheetDescriptionProperties) => (
  <SheetDescriptionPrimitive ref={ref} className={cn('text-sm text-muted-foreground', className)} {...properties} />
);
SheetDescription.displayName = SheetDescriptionPrimitive.displayName;

export {
  Sheet,
  SheetPortal,
  SheetOverlay,
  SheetTrigger,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetFooter,
  SheetTitle,
  SheetDescription,
};
